/**
 * 官网落地页
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Layout, Typography, Row, Col, Card, Space } from 'antd';
import {
  CalendarOutlined,
  FileOutlined,
  BellOutlined,
  TeamOutlined,
  CloudOutlined,
  SafetyOutlined,
  BookOutlined,
  RobotOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  BankOutlined,
} from '@ant-design/icons';
import './LandingPage.css';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || '';
const COMPANY_ADDRESS = process.env.REACT_APP_COMPANY_ADDRESS || '';
const COMPANY_PHONE = process.env.REACT_APP_COMPANY_PHONE || '';
const COMPANY_EMAIL = process.env.REACT_APP_COMPANY_EMAIL || '';

const features = [
  { icon: <CalendarOutlined />, title: '排课系统', desc: '日历/列表视图排课，Meet 链接与 FrameVR 场景，Google 日历同步与多端提醒。' },
  { icon: <FileOutlined />, title: '文件管理', desc: '文档上传、分类、预览与下载，支持 PDF/Word/Excel/图片/视频/ZIP，空间与权限管理。' },
  { icon: <BellOutlined />, title: '多通道通知', desc: '课程变更自动通知：邮件、企业微信、WhatsApp，可查看每次发送状态历史。' },
  { icon: <BookOutlined />, title: '知识库', desc: '对接外部知识库服务，支持知识库管理、文档录入与检索，对话时可选择知识库进行基于文档的问答。' },
  { icon: <RobotOutlined />, title: '智能体', desc: '对接外部智能体服务，支持多智能体配置与专属能力，对话中可切换智能体、设置欢迎语与个性化回复。' },
  { icon: <TeamOutlined />, title: '用户与权限', desc: '会员与用户组管理，声明式权限控制，角色与模块灵活配置。' },
  { icon: <CloudOutlined />, title: '云文档与日历', desc: 'Google 日历同步、云盘同步，上课提醒与一键启动链接。' },
  { icon: <SafetyOutlined />, title: '安全与部署', desc: 'JWT 认证、验证码登录、HTTPS、Docker 容器化部署。' },
];

const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Layout className="landing-page">
      <Header className={`landing-header ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="header-inner">
          <Link to="/" className="header-logo">
            <img src="/assets/logo.png" alt="" className="header-logo-icon" />
            <img src="/assets/logo-text.png" alt="blooart CAMPUS" className="header-logo-text" />
          </Link>
          <nav className="header-nav">
            <a href="#features">功能</a>
            <a href="#contact">联系我们</a>
            <Link to="/login">
              <Button type="text" className="nav-btn-text">登录</Button>
            </Link>
            <Link to="/login">
              <Button type="primary" className="nav-btn-primary">进入系统</Button>
            </Link>
          </nav>
        </div>
      </Header>

      <Content className="landing-body">
        <section className="hero">
          <div className="hero-bg" />
          <div className="hero-inner">
            <h1 className="hero-title">智能排课 · 文件管理 · 多端通知</h1>
            <p className="hero-desc">
              面向机构与学校的教育管理平台，排课、文档、通知与权限一体化，支持 Google 日历与企业微信，
              仅向已授权的老师与学生发送课程相关通知。
            </p>
            <div className="hero-actions">
              <Link to="/login">
                <Button type="primary" size="large" className="hero-cta">免费使用</Button>
              </Link>
              <a href="#features" className="hero-link">了解功能</a>
            </div>
          </div>
        </section>

        <section id="features" className="section section-features">
          <h2 className="section-title">产品功能</h2>
          <p className="section-subtitle">排课、文件、通知、知识库、智能体、权限与云协作，一站解决教学管理需求</p>
          <Row gutter={[24, 24]}>
            {features.map((item, i) => (
              <Col xs={24} sm={12} lg={8} key={i}>
                <Card className="feature-card" bordered={false}>
                  <span className="feature-icon">{item.icon}</span>
                  <h3 className="feature-title">{item.title}</h3>
                  <p className="feature-desc">{item.desc}</p>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        <section className="section section-cta">
          <div className="cta-box">
            <h3 className="cta-title">立即开始使用</h3>
            <p className="cta-desc">登录后即可使用排课、文件管理与多通道通知</p>
            <Link to="/login">
              <Button type="primary" size="large" className="cta-btn">登录 / 进入系统</Button>
            </Link>
          </div>
        </section>
        <section id="contact" className="section section-contact">
          <h2 className="section-title">联系我们</h2>
          <div className="contact-block">
            {COMPANY_NAME ? (
              <div className="contact-item">
                <BankOutlined className="contact-icon" />
                <div><span className="contact-label">公司名称</span><span className="contact-value">{COMPANY_NAME}</span></div>
              </div>
            ) : null}
            {COMPANY_ADDRESS ? (
              <div className="contact-item">
                <EnvironmentOutlined className="contact-icon" />
                <div><span className="contact-label">营业地址</span><span className="contact-value">{COMPANY_ADDRESS}</span></div>
              </div>
            ) : null}
            {COMPANY_PHONE ? (
              <div className="contact-item">
                <PhoneOutlined className="contact-icon" />
                <div><span className="contact-label">联系电话</span><a href={`tel:${COMPANY_PHONE.replace(/\s/g, '')}`} className="contact-value contact-link">{COMPANY_PHONE}</a></div>
              </div>
            ) : null}
            {COMPANY_EMAIL ? (
              <div className="contact-item">
                <MailOutlined className="contact-icon" />
                <div><span className="contact-label">联系邮箱</span><a href={`mailto:${COMPANY_EMAIL}`} className="contact-value contact-link">{COMPANY_EMAIL}</a></div>
              </div>
            ) : null}
            {!COMPANY_NAME && !COMPANY_ADDRESS && !COMPANY_PHONE && !COMPANY_EMAIL && (
              <p className="contact-placeholder">请在环境变量中配置 REACT_APP_COMPANY_NAME、REACT_APP_COMPANY_ADDRESS、REACT_APP_COMPANY_PHONE、REACT_APP_COMPANY_EMAIL，并与 Meta 商务管理平台中的信息一致。</p>
            )}
          </div>
        </section>
      </Content>

      <Footer className="landing-footer">
        <div className="footer-inner">
          <Link to="/" className="footer-logo">
            <img src="/assets/logo.png" alt="" />
            <img src="/assets/logo-text.png" alt="blooart CAMPUS" />
          </Link>
          <span className="footer-copy">AI 教育管理平台</span>
          <div className="footer-links">
            <Link to="/">首页</Link>
            <a href="#contact">联系我们</a>
            <Link to="/privacy">隐私政策</Link>
            <Link to="/terms">服务条款</Link>
            <Link to="/login">登录</Link>
          </div>
        </div>
      </Footer>
    </Layout>
  );
};

export default LandingPage;
